// src/features/banquetOwnerPanel/model/banquetFormModel.js
//
// Pure (no React, no auth) data model shared by the owner-login dashboard
// (BanquetOwnerDashboardPage.jsx) and the public token-based owner-fill form
// (OwnerFillFormPage.jsx) — the option lists, the empty/normalized form shape, and the
// form-state -> Firestore-payload mapping used to render and save the same 16-section form
// from two different entry points without forking the logic.

export const venueTypes = [
  "Banquet Hall",
  "Hotel Banquet",
  "Farmhouse",
  "Lawn",
  "Resort",
  "Club",
  "Community Center",
  "Rooftop",
  "Restaurant Party Hall",
  "Marriage Garden",
  "Conference Hall",
  "Party Hall",
  "Poolside Venue",
];
export const floorOptions = ["Ground Floor", "First Floor", "Second Floor", "Third Floor", "Fourth Floor", "Fifth Floor"];
export const capacityOptions = Array.from({ length: 20 }, (_, index) => String((index + 1) * 25));
export const minGuestRanges = Array.from({ length: 18 }, (_, index) => `${25 + index * 25}-${50 + index * 25}`);
export const oneTo = (count) => Array.from({ length: count }, (_, index) => String(index + 1));
export const hallCountOptions = ["1", "2", "3", "4", "5+"];
export const createEmptyHall = () => ({ floor: "Ground Floor", hallName: "", seatingCapacity: "", floatingCapacity: "" });
export const parkingTypes = ["Ground Parking", "Basement Parking", "Multi Level Parking", "Road Side Parking", "Open Parking", "Covered Parking"];
export const pricingModels = ["Per Person", "Hall Rental", "Farmhouse Rental"];
export const foodTypes = ["Veg Only", "Non Veg Only", "Veg + Non Veg"];
export const parkingCharges = ["Free", "Paid", "Valet Included", "Valet Extra"];
export const bookingAdvance = ["10%", "15%", "20%", "25%", "30%", "40%", "50%"];
export const liveCounters = ["Chaat Counter", "Chinese Counter", "Italian Counter", "South Indian Counter", "Mocktail Counter", "Coffee Counter", "Dessert Counter", "Ice Cream Counter"];
export const specialFood = ["Jain Food Available", "Satvik Food Available", "Kids Menu Available", "Corporate Buffet Available", "Custom Menu Available"];
export const foodServingStyles = ["Buffet", "Table Service", "Both Available"];
export const eventTypeGroups = {
  "Wedding Events": ["Wedding", "Reception", "Engagement", "Ring Ceremony", "Haldi", "Mehendi", "Sangeet"],
  "Birthday Events": ["Birthday Party", "First Birthday", "Kids Birthday", "Adult Birthday", "Milestone Birthday"],
  "Baby Events": ["Baby Shower", "Godh Bharai", "Welcome Baby", "Naming Ceremony", "Mundan Ceremony"],
  "Social Events": ["Anniversary", "Silver Jubilee", "Golden Jubilee", "Family Gathering", "Kitty Party", "Get Together"],
  "Corporate Events": ["Corporate Event", "Conference", "Seminar", "Product Launch", "Award Function", "Training Program"],
  "Exhibitions & Others": ["Exhibition", "Trade Show", "Cultural Event", "Fashion Show", "School Function", "College Function"],
};
// Themes intentionally reference occasion names (Haldi Decoration, Reception Decoration, etc.)
// even though Event Types Supported (section 12) also lists those occasions — the two ask
// different questions ("does the venue host this event type" vs "can DecorZoom decorate in
// this style/theme"), unlike the eventTypesSupported field removed from this section
// previously, which was a literal duplicate of the same "supported event type" question.
export const themeOptions = [
  "Balloon Decoration", "Theme Decoration", "Ring Decoration", "Stage Decoration", "Floral Decoration", "Artificial Flower Decoration", "LED Decoration", "Entrance Decoration", "Welcome Board", "Photo Booth", "Selfie Point", "Kids Theme Setup", "Premium Luxury Setup",
  "First Birthday Theme", "Haldi Decoration", "Mehendi Decoration", "Sangeet / Cocktail Party Decoration", "Wedding Stage Decoration", "Reception Decoration", "Engagement Decoration", "Anniversary Decoration", "Baby Shower / Welcome Baby Decoration", "Birthday Decoration", "Corporate Event Decoration", "Naming Ceremony Decoration", "Housewarming Decoration",
  "Bride/Groom SFX Entry", "Bride/Groom Dhol Entry", "Bride/Groom Cold Pyro Entry", "Bride/Groom Floral Entry", "Bride/Groom Palanquin/Doli Entry",
];
export const materialOptions = [
  "Imported Balloons", "Chrome Balloons", "Pastel Balloons", "Fresh Flowers", "Artificial Flowers", "LED Lights", "Neon Lights", "Fabric Draping", "Wooden Setup", "Acrylic Setup",
  "Backdrop Curtains", "Fairy Lights / String Lights", "Cold Pyro / SFX Machines", "Confetti Cannons", "Floral Arch / Mandap Setup", "Theme Props & Cutouts", "Red Carpet",
];
// DJ/Live Music, Anchor/Host, and Cake/Dessert Table are covered by the existing DJ + Live
// Singer, Anchor / Emcee, and Cake Table options below. Live food counters (e.g. Chaat
// Counter) are covered by Food & Catering's dedicated Live Counters section — not repeated
// here.
export const serviceOptions = [
  "Cake Table", "Return Gifts", "Tattoo Artist", "Face Painting", "Magic Show", "Game Coordinator", "Mascot Entry", "Bride Entry", "Groom Entry", "Photography", "Videography", "DJ", "Live Singer", "Dhol", "Anchor / Emcee",
  "Drone Coverage", "Cold Pyro / Fireworks Show", "Entry Choreography (Dance Troupe)", "Mehendi Artist Arrangement", "Makeup Artist Arrangement",
];
export const alcoholAreas = ["Hall", "Lawn", "Rooftop", "Poolside", "Entire Venue"];
export const barTypes = ["Permanent Bar", "Temporary Bar", "Mobile Bar Setup"];
export const eventsSupported = ["Breakfast", "Lunch", "High Tea", "Dinner", "Full Day Event", "Overnight Event"];
export const djTimingLimits = ["10 PM", "11 PM", "12 AM", "Venue Rules Apply"];
export const mediaHighlights = ["Day View", "Night View", "Wedding Setup", "Birthday Setup", "Corporate Setup", "Poolside", "Rooftop", "Lawn Setup"];
// Parking, lift, wheelchair access, bridal room, guest rooms, catering, decoration, and
// alcohol are each asked exactly once in their own dedicated section (Capacity, Food &
// Catering, Decoration & Event Services, Alcohol Policy, Parking) — intentionally NOT
// repeated here as marketing chips, so an owner never has two different places to answer
// the same question with two different answers.
export const facilityGroups = {
  "Venue Facilities": ["AC Hall", "Non AC Hall", "Rooftop Venue", "Garden Area", "Open Lawn", "Swimming Pool", "Kids Play Area"],
  "Event Infrastructure": ["Stage", "LED Wall", "Projector", "DJ Setup", "Sound System", "Dance Floor", "WiFi", "Power Backup"],
  "Guest Comfort": ["Green Room", "Security Guards", "CCTV Surveillance"],
  "Special Permissions": ["Firecracker Allowed", "Late Night Events Allowed"],
};
export const highlightGroups = {
  "Location Highlights": ["Near Metro", "Near Highway", "Near Airport", "Near Railway Station", "Prime Location", "Easy Accessibility"],
  "Venue Type Highlights": ["Luxury Venue", "Premium Venue", "Budget Friendly", "Family Friendly", "Corporate Friendly"],
  "Space Highlights": ["Open Lawn", "Rooftop Venue", "Poolside Venue", "Garden Venue", "Indoor Venue", "Outdoor Venue"],
  "Event Highlights": ["Wedding Specialist", "Birthday Specialist", "Corporate Event Specialist", "Baby Shower Specialist", "Multi Event Venue"],
  "Decor & Experience": ["Premium Decor", "Custom Decoration Available", "Theme Decoration Available", "Instagram Worthy Venue", "Grand Entrance Available"],
  "Food & Hospitality": ["Multi Cuisine Available", "Live Counters Available", "Premium Hospitality"],
};
export const statusStyles = {
  available: "bg-emerald-50 text-emerald-800 border-emerald-200",
  lunchBooked: "bg-yellow-50 text-yellow-800 border-yellow-200",
  dinnerBooked: "bg-orange-50 text-orange-800 border-orange-200",
  fullDayBooked: "bg-red-50 text-red-800 border-red-200",
  blocked: "bg-slate-100 text-slate-700 border-slate-200",
};
export const statusLabels = {
  available: "Available",
  lunchBooked: "Lunch Booked",
  dinnerBooked: "Dinner Booked",
  fullDayBooked: "Full Day Booked",
  blocked: "Blocked",
};

export const emptyForm = {
  basicDetails: { banquetName: "", venueTypes: [] },
  contactDetails: { contactPerson: "", mobileNumber: "", alternateMobile: "", whatsappNumber: "", sameAsMobile: false, email: "", website: "", instagram: "", facebook: "", youtube: "" },
  locationDetails: { city: "", locality: "", fullAddress: "", landmark: "", pincode: "", googleMapLink: "", latitude: "", longitude: "", googlePlaceId: "", googleRating: "", googleRatingsTotal: "" },
  capacityDetails: {
    seatingCapacity: "",
    floatingCapacity: "",
    minimumGuestRange: "",
    halls: [],
    roomsAvailable: false,
    numberOfRooms: "",
    complimentaryRoomAvailable: false,
    numberOfComplimentaryRooms: "",
    bridalRoomAvailable: false,
    numberOfBridalRooms: "",
    liftAvailable: false,
    passengerLiftCount: "",
    serviceLiftAvailable: false,
    wheelchairAccessible: false,
  },
  pricingDetails: {
    pricingModels: [],
    foodType: "",
    vegStartingFrom: "",
    nonVegStartingFrom: "",
    hallRentalStartingFrom: "",
    farmhouseRentalStartingFrom: "",
    bookingAdvanceRequired: "",
  },
  facilities: [],
  foodCatering: { inhouseCatering: false, outsideCateringAllowed: false, kitchenAvailable: false, liveCountersAvailable: false, liveCounters: [], specialFoodServices: [], foodServingStyle: "", foodTastingAvailable: false },
  decorationServices: { inhouseDecoration: false, decorationTeam: "", outsideDecoratorAllowed: false, decorPackagesAvailable: false, decorationStartingPrice: "", decorationThemesAvailable: [], decorationMaterialsAvailable: [], additionalServicesAvailable: [] },
  alcoholPolicy: { alcoholAllowed: false, inhouseAlcoholAvailable: false, outsideAlcoholAllowed: false, liquorLicenseAvailable: false, corkageChargesApplicable: false, corkageCharges: "", alcoholServiceAreas: [], barCounterAvailable: false, barType: "", bartenderAvailable: false, mocktailCounterAvailable: false },
  timingDetails: { openingTime: "", closingTime: "", eventsSupported: [], fullDayEventAvailable: false, overnightEventAllowed: false, musicDjTimingLimit: "" },
  parkingDetails: { parkingAvailable: false, totalParkingCapacity: "", carParkingCapacity: "", bikeParkingCapacity: "", valetParking: false, parkingTypes: [], busParkingAvailable: false, parkingCharges: "" },
  eventTypes: [],
  media: { coverPhotos: [], galleryPhotos: [], videoTeasers: [], droneVideos: [], tour360: { video: null, link: "" }, socialVideoLinks: [], mediaHighlights: [], primaryCoverIndex: 0 },
  highlights: [],
  packagesMenu: [],
  availabilityCalendar: { showToPublic: true, dates: {}, bookedSlots: {}, bookedDates: {} },
};

export const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
export const toNumber = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
export const text = (value) => String(value || "").trim();
export const unique = (items) => [...new Set((items || []).filter(Boolean))];
export const mediaUrl = (item) => (typeof item === "string" ? item : item?.url || "");
export const cleanMediaItems = (items = []) => items.map((item) => ({ name: item.name || "", url: item.url || "", status: item.url ? "uploaded" : item.status || "selected", error: item.error || "" })).filter((item) => item.url || item.name);
export const cleanPackagesMenu = (items = []) =>
  items
    .filter((item) => item && item.url)
    .map((item) => ({ label: text(item.label) || item.fileName || "Document", fileName: item.fileName || "", url: item.url }));
export const cleanHalls = (halls = []) =>
  halls
    .filter((hall) => hall && (text(hall.hallName) || hall.seatingCapacity || hall.floatingCapacity))
    .map((hall) => ({ floor: hall.floor || "", hallName: text(hall.hallName), seatingCapacity: toNumber(hall.seatingCapacity), floatingCapacity: toNumber(hall.floatingCapacity) }));
export const dateKey = (date) => date.toISOString().split("T")[0];
export const mediaRoles = ["coverPhotos", "galleryPhotos", "videoTeasers", "droneVideos"];
export const formatSavedTime = (date) =>
  date
    ? date.toLocaleTimeString("en-IN", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "";
export const timestampToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
export const toBookedSlots = (dates = {}) =>
  Object.entries(dates).reduce((acc, [key, status]) => {
    acc[key] = {
      lunch: status === "lunchBooked" || status === "fullDayBooked" || status === "blocked",
      dinner: status === "dinnerBooked" || status === "fullDayBooked" || status === "blocked",
      status,
    };
    return acc;
  }, {});

export const formFromBanquet = (banquet = {}) => {
  const media = banquet.media || {};
  return {
    ...emptyForm,
    basicDetails: {
      ...emptyForm.basicDetails,
      ...(banquet.basicDetails || {}),
      banquetName: banquet.basicDetails?.banquetName || banquet.name || banquet.title || "",
      venueTypes: banquet.basicDetails?.venueTypes?.length ? banquet.basicDetails.venueTypes : banquet.venueType ? [banquet.venueType] : [],
    },
    contactDetails: { ...emptyForm.contactDetails, ...(banquet.contactDetails || {}), mobileNumber: banquet.contactDetails?.mobileNumber || banquet.phone || "", whatsappNumber: banquet.contactDetails?.whatsappNumber || banquet.whatsappNumber || "", email: banquet.contactDetails?.email || banquet.email || "" },
    locationDetails: {
      ...emptyForm.locationDetails,
      ...(banquet.locationDetails || {}),
      city: banquet.locationDetails?.city || banquet.city || "",
      locality: banquet.locationDetails?.locality || banquet.locality || "",
      fullAddress: banquet.locationDetails?.fullAddress || (typeof banquet.address === "string" ? banquet.address : banquet.address?.line1) || "",
      pincode: banquet.locationDetails?.pincode || banquet.pincode || banquet.address?.pincode || "",
      googleMapLink: banquet.locationDetails?.googleMapLink || banquet.googleMapsUrl || banquet.mapUrl || "",
      latitude: banquet.locationDetails?.latitude ?? banquet.latitude ?? "",
      longitude: banquet.locationDetails?.longitude ?? banquet.longitude ?? "",
      googlePlaceId: banquet.locationDetails?.googlePlaceId || banquet.googlePlaceId || "",
      googleRating: banquet.locationDetails?.googleRating ?? banquet.googleRating ?? "",
      googleRatingsTotal: banquet.locationDetails?.googleRatingsTotal ?? banquet.googleRatingsTotal ?? "",
    },
    capacityDetails: { ...emptyForm.capacityDetails, ...(banquet.capacityDetails || {}), seatingCapacity: String(banquet.capacityDetails?.seatingCapacity ?? banquet.guestMin ?? ""), floatingCapacity: String(banquet.capacityDetails?.floatingCapacity ?? banquet.guestMax ?? "") },
    pricingDetails: { ...emptyForm.pricingDetails, ...(banquet.pricingDetails || {}), foodType: banquet.pricingDetails?.foodType || banquet.foodType || "", vegStartingFrom: String(banquet.pricingDetails?.vegStartingFrom ?? banquet.vegPrice ?? ""), nonVegStartingFrom: String(banquet.pricingDetails?.nonVegStartingFrom ?? banquet.nonVegPrice ?? "") },
    facilities: Array.isArray(banquet.facilities) ? banquet.facilities : [],
    foodCatering: { ...emptyForm.foodCatering, ...(banquet.foodCatering || {}) },
    decorationServices: { ...emptyForm.decorationServices, ...(banquet.decorationServices || {}) },
    alcoholPolicy: { ...emptyForm.alcoholPolicy, ...(typeof banquet.alcoholPolicy === "object" ? banquet.alcoholPolicy : {}) },
    timingDetails: { ...emptyForm.timingDetails, ...(banquet.timingDetails || {}) },
    parkingDetails: { ...emptyForm.parkingDetails, ...(banquet.parkingDetails || {}) },
    eventTypes: Array.isArray(banquet.eventTypes) ? banquet.eventTypes : [],
    media: {
      ...emptyForm.media,
      ...media,
      coverPhotos: Array.isArray(media.coverPhotos) ? media.coverPhotos : banquet.coverImage ? [{ url: banquet.coverImage, name: "Primary cover", status: "uploaded" }] : [],
      galleryPhotos: Array.isArray(media.galleryPhotos) ? media.galleryPhotos : Array.isArray(banquet.images) ? banquet.images.map((url) => ({ url, name: "Gallery photo", status: "uploaded" })) : [],
      videoTeasers: Array.isArray(media.videoTeasers) ? media.videoTeasers : banquet.teaserVideoUrl ? [{ url: banquet.teaserVideoUrl, name: "Video teaser", status: "uploaded" }] : [],
      droneVideos: Array.isArray(media.droneVideos) ? media.droneVideos : [],
      tour360: media.tour360 || emptyForm.media.tour360,
      socialVideoLinks: Array.isArray(media.socialVideoLinks) ? media.socialVideoLinks : [],
    },
    highlights: Array.isArray(banquet.highlights) ? banquet.highlights : [],
    packagesMenu: Array.isArray(banquet.packagesMenu) ? banquet.packagesMenu : [],
    availabilityCalendar: { ...emptyForm.availabilityCalendar, ...(banquet.availabilityCalendar || {}), dates: banquet.availabilityCalendar?.dates || banquet.bookedDates || {} },
  };
};

// Pure form-state -> Firestore-payload mapping, shared by the owner-login dashboard (where
// `owner`/`selectedBanquet` come from an authenticated session) and the public owner-fill
// form (where `owner` is null and `selectedBanquet` is the banquet loaded by token) — the
// `owner?.uid || ""` style fallbacks below already tolerate a null owner.
export const buildBanquetFormPayload = ({ form, selectedBanquet, owner, banquetId, previewSlug }) => {
  const payloadForm = form;
  const contactDetails = {
    ...payloadForm.contactDetails,
    whatsappNumber: payloadForm.contactDetails.sameAsMobile ? payloadForm.contactDetails.mobileNumber : payloadForm.contactDetails.whatsappNumber,
  };
  const coverPhotos = cleanMediaItems(payloadForm.media.coverPhotos);
  const galleryPhotos = cleanMediaItems(payloadForm.media.galleryPhotos);
  const primaryCoverIndex = Math.min(Math.max(Number(payloadForm.media.primaryCoverIndex) || 0, 0), Math.max(coverPhotos.length - 1, 0));
  const primaryCover = coverPhotos[primaryCoverIndex]?.url || coverPhotos[0]?.url || "";
  const imageUrls = unique([...coverPhotos.map((item) => item.url), ...galleryPhotos.map((item) => item.url)].filter(Boolean));
  const legacyVenueType = payloadForm.basicDetails.venueTypes[0] || "";
  const guestMin = toNumber(payloadForm.capacityDetails.seatingCapacity);
  const guestMax = toNumber(payloadForm.capacityDetails.floatingCapacity);
  const vegPrice = toNumber(payloadForm.pricingDetails.vegStartingFrom);
  const nonVegPrice = toNumber(payloadForm.pricingDetails.nonVegStartingFrom);
  const dates = payloadForm.availabilityCalendar.dates || {};

  return {
    ...(selectedBanquet || {}),
    id: banquetId,
    slug: previewSlug,
    title: payloadForm.basicDetails.banquetName,
    name: payloadForm.basicDetails.banquetName,
    banquetName: payloadForm.basicDetails.banquetName,
    venueType: legacyVenueType,
    basicDetails: payloadForm.basicDetails,
    contactDetails,
    locationDetails: payloadForm.locationDetails,
    capacityDetails: { ...payloadForm.capacityDetails, halls: cleanHalls(payloadForm.capacityDetails.halls) },
    pricingDetails: payloadForm.pricingDetails,
    foodCatering: payloadForm.foodCatering,
    decorationServices: payloadForm.decorationServices,
    alcoholPolicy: payloadForm.alcoholPolicy,
    timingDetails: payloadForm.timingDetails,
    parkingDetails: payloadForm.parkingDetails,
    media: {
      ...payloadForm.media,
      coverPhotos,
      galleryPhotos,
      videoTeasers: cleanMediaItems(payloadForm.media.videoTeasers),
      droneVideos: cleanMediaItems(payloadForm.media.droneVideos),
      primaryCoverIndex,
    },
    highlights: payloadForm.highlights,
    packagesMenu: cleanPackagesMenu(payloadForm.packagesMenu),
    city: payloadForm.locationDetails.city,
    locality: payloadForm.locationDetails.locality,
    address: { line1: payloadForm.locationDetails.fullAddress, pincode: payloadForm.locationDetails.pincode, latitude: toNumber(payloadForm.locationDetails.latitude), longitude: toNumber(payloadForm.locationDetails.longitude) },
    pincode: payloadForm.locationDetails.pincode,
    mapUrl: payloadForm.locationDetails.googleMapLink,
    googleMapsUrl: payloadForm.locationDetails.googleMapLink,
    latitude: toNumber(payloadForm.locationDetails.latitude),
    longitude: toNumber(payloadForm.locationDetails.longitude),
    phone: contactDetails.mobileNumber,
    whatsappNumber: contactDetails.whatsappNumber,
    email: contactDetails.email,
    website: contactDetails.website,
    guestMin,
    guestMax,
    guestRange: { min: guestMin, max: guestMax },
    vegPrice,
    nonVegPrice,
    pricing: { currency: "INR", vegPrice, nonVegPrice, hallRentalStartingFrom: toNumber(payloadForm.pricingDetails.hallRentalStartingFrom), farmhouseRentalStartingFrom: toNumber(payloadForm.pricingDetails.farmhouseRentalStartingFrom), decorationStartingFrom: toNumber(payloadForm.decorationServices.decorationStartingPrice) },
    parkingAvailable: payloadForm.parkingDetails.parkingAvailable,
    parkingCapacity: Number(String(payloadForm.parkingDetails.totalParkingCapacity || "").replace(/\D/g, "")) || 0,
    valetParking: payloadForm.parkingDetails.valetParking,
    roomsAvailable: payloadForm.capacityDetails.roomsAvailable,
    roomCount: toNumber(payloadForm.capacityDetails.numberOfRooms) || 0,
    bridalRoom: payloadForm.capacityDetails.bridalRoomAvailable,
    lift: payloadForm.capacityDetails.liftAvailable,
    acHall: payloadForm.facilities.includes("AC Hall"),
    powerBackup: payloadForm.facilities.includes("Power Backup"),
    wheelchairFriendly: payloadForm.capacityDetails.wheelchairAccessible,
    liquorAllowed: payloadForm.alcoholPolicy.alcoholAllowed,
    djAllowed: payloadForm.facilities.includes("DJ Setup") || payloadForm.decorationServices.additionalServicesAvailable.includes("DJ"),
    lateNightAllowed: payloadForm.facilities.includes("Late Night Events Allowed"),
    outsideCateringAllowed: payloadForm.foodCatering.outsideCateringAllowed,
    eventTypes: payloadForm.eventTypes,
    facilities: payloadForm.facilities,
    tags: unique([...payloadForm.facilities, ...payloadForm.highlights]).slice(0, 30),
    coverImage: primaryCover,
    coverImageUrl: primaryCover,
    images: imageUrls,
    galleryImages: galleryPhotos.map(mediaUrl).filter(Boolean),
    teaserVideoUrl: payloadForm.media.videoTeasers[0]?.url || "",
    availabilityCalendar: { ...payloadForm.availabilityCalendar, bookedDates: dates, bookedSlots: toBookedSlots(dates) },
    bookedDates: dates,
    bookedSlots: toBookedSlots(dates),
    ownerId: selectedBanquet?.ownerId || owner?.uid || "",
    ownerEmail: selectedBanquet?.ownerEmail || owner?.email || "",
    assignedUserIds: selectedBanquet?.assignedUserIds?.length ? selectedBanquet.assignedUserIds : owner?.uid ? [owner.uid] : [],
    assignedUserEmails: selectedBanquet?.assignedUserEmails?.length ? selectedBanquet.assignedUserEmails : owner?.email ? [owner.email] : [],
    createdByOwner: selectedBanquet?.createdByOwner ?? true,
  };
};

// Pure function of `form` alone — a non-blocking "here's what's still empty" checklist, not a
// hard validation gate (submission is always allowed; see BanquetOwnerDashboardPage's submit()
// and OwnerFillFormPage's equivalent).
export const computeMissingRequiredFields = (form) => {
  const coverCount = form.media.coverPhotos.length;
  const galleryCount = form.media.galleryPhotos.length;
  const pricingModelsSelected = form.pricingDetails.pricingModels.length;
  const perPersonSelected = form.pricingDetails.pricingModels.includes("Per Person");
  const hallRentalSelected = form.pricingDetails.pricingModels.includes("Hall Rental");
  const farmhouseRentalSelected = form.pricingDetails.pricingModels.includes("Farmhouse Rental");
  return [
    { sectionIndex: "1", sectionName: "Basic Details", label: "Banquet Name", value: form.basicDetails.banquetName },
    { sectionIndex: "1", sectionName: "Basic Details", label: "Venue Type", value: form.basicDetails.venueTypes.length },
    { sectionIndex: "2", sectionName: "Contact Details", label: "Contact Person", value: form.contactDetails.contactPerson },
    { sectionIndex: "2", sectionName: "Contact Details", label: "Mobile Number", value: form.contactDetails.mobileNumber },
    { sectionIndex: "2", sectionName: "Contact Details", label: "Email", value: form.contactDetails.email },
    { sectionIndex: "3", sectionName: "Location & Map Details", label: "City", value: form.locationDetails.city },
    { sectionIndex: "3", sectionName: "Location & Map Details", label: "Locality / Area", value: form.locationDetails.locality },
    { sectionIndex: "3", sectionName: "Location & Map Details", label: "Full Address", value: form.locationDetails.fullAddress },
    { sectionIndex: "3", sectionName: "Location & Map Details", label: "Google Map Link", value: form.locationDetails.googleMapLink },
    { sectionIndex: "4", sectionName: "Venue Capacity & Structure", label: "Seating Capacity", value: form.capacityDetails.seatingCapacity },
    { sectionIndex: "4", sectionName: "Venue Capacity & Structure", label: "Floating Capacity", value: form.capacityDetails.floatingCapacity },
    { sectionIndex: "4", sectionName: "Venue Capacity & Structure", label: "Minimum Guest Range", value: form.capacityDetails.minimumGuestRange },
    { sectionIndex: "5", sectionName: "Pricing", label: "Pricing Model", value: pricingModelsSelected },
    { sectionIndex: "5", sectionName: "Pricing", label: "Food Type", value: !perPersonSelected || form.pricingDetails.foodType },
    { sectionIndex: "5", sectionName: "Pricing", label: "Veg Starting From", value: !perPersonSelected || form.pricingDetails.foodType === "Non Veg Only" || form.pricingDetails.vegStartingFrom },
    { sectionIndex: "5", sectionName: "Pricing", label: "Non Veg Starting From", value: !perPersonSelected || form.pricingDetails.foodType === "Veg Only" || form.pricingDetails.nonVegStartingFrom },
    { sectionIndex: "5", sectionName: "Pricing", label: "Hall Rental Starting From", value: !hallRentalSelected || form.pricingDetails.hallRentalStartingFrom },
    { sectionIndex: "5", sectionName: "Pricing", label: "Farmhouse Rental Starting From", value: !farmhouseRentalSelected || form.pricingDetails.farmhouseRentalStartingFrom },
    { sectionIndex: "10", sectionName: "Event Timings & Operating Hours", label: "Opening Time", value: form.timingDetails.openingTime },
    { sectionIndex: "10", sectionName: "Event Timings & Operating Hours", label: "Closing Time", value: form.timingDetails.closingTime },
    { sectionIndex: "13", sectionName: "Photos, Videos & Media", label: "At least one cover photo", value: coverCount >= 1 },
    { sectionIndex: "13", sectionName: "Photos, Videos & Media", label: "At least 10 gallery photos", value: galleryCount >= 10 },
  ].filter((item) => !item.value);
};
